import {
    ArrowDownTrayIcon,
    ArrowPathIcon,
    CheckCircleIcon,
    DocumentArrowUpIcon,
    ExclamationTriangleIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { SelectField } from '@/form-control/fields'
import ROUTES from '@/ROUTES'

import { api } from '@/services/api.service'

import Button from './Button'

interface FileUploadProps {
    onUploadComplete?: (response: ImportResponse) => void

    acceptedFormats?: string[]
    maxFileSize?: number
}

interface ImportResponse {
    success: boolean
    imported: number
    failed?: number
    total?: number
    errors?: ValidationError[]
    warnings?: string[]
    savedProviders?: any[]
}

interface ValidationError {
    row: number
    field: string
    message: string
    value?: any
}

interface UploadState {
    status: 'idle' | 'parsing' | 'success' | 'error'
    fileName?: string
    response?: ImportResponse
}

export const FileUploadComponent: React.FC<FileUploadProps> = ({
    onUploadComplete,

    acceptedFormats = ['.csv', '.xlsx', '.xls'],
    maxFileSize = 10,
}) => {
    const [uploadState, setUploadState] = useState<UploadState>({ status: 'idle' })
    const [dragActive, setDragActive] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [selectedCategory, setSelectedCategory] = useState('')

    const handleDrag = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true)
        } else if (e.type === 'dragleave') {
            setDragActive(false)
        }
    }, [])

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setDragActive(false)

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0])
        }
    }, [])

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            handleFile(e.target.files[0])
        }
    }

    const readFileContent = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()

            reader.onload = (e) => {
                if (e.target?.result) {
                    if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
                        const arrayBuffer = e.target.result as ArrayBuffer
                        const base64 = btoa(
                            new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
                        )
                        resolve(base64)
                    } else {
                        resolve(e.target.result as string)
                    }
                } else {
                    reject(new Error('Failed to read file'))
                }
            }

            reader.onerror = () => reject(new Error('Failed to read file'))

            if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
                reader.readAsArrayBuffer(file)
            } else {
                reader.readAsText(file)
            }
        })
    }

    const handleFile = async (file: File) => {
        const fileSizeMB = file.size / (1024 * 1024)
        if (fileSizeMB > maxFileSize) {
            const errorResponse: ImportResponse = {
                success: false,
                imported: 0,
                failed: 0,
                total: 0,
                errors: [
                    {
                        row: 0,
                        field: 'file',
                        message: `File size exceeds ${maxFileSize}MB limit (${fileSizeMB.toFixed(2)}MB)`,
                    },
                ],
            }
            setUploadState({ status: 'error', fileName: file.name, response: errorResponse })
            onUploadComplete?.(errorResponse)
            return
        }

        const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`
        if (!acceptedFormats.some((format) => fileExtension === format)) {
            const errorResponse: ImportResponse = {
                success: false,
                imported: 0,
                failed: 0,
                total: 0,
                errors: [
                    {
                        row: 0,
                        field: 'file',
                        message: `Invalid file format. Accepted formats: ${acceptedFormats.join(', ')}`,
                    },
                ],
            }
            setUploadState({ status: 'error', fileName: file.name, response: errorResponse })
            onUploadComplete?.(errorResponse)
            return
        }

        setUploadState({
            status: 'parsing',
            fileName: file.name,
        })

        try {
            const fileContent = await readFileContent(file)
            const fileType = fileExtension === '.csv' ? 'csv' : 'excel'

            const response = await api<ImportResponse>(`${ROUTES.app.provider}/import`, {
                fileContent,
                fileName: file.name,
                fileType,
                categoryId: selectedCategory,
            })

            const importResponse: ImportResponse = {
                success: response.data?.success ?? false,
                imported: response.data?.imported ?? 0,
                failed: response.data?.failed ?? 0,
                total: response.data?.total ?? 0,
                warnings: response.data?.warnings ?? [],
                savedProviders: response.data?.savedProviders ?? [],
                errors: Array.isArray(response.data?.errors)
                    ? response.data.errors.map((err: any, i: number) =>
                          typeof err === 'string' ? { row: 0, field: 'file', message: err } : err
                      )
                    : null,
            }

            setUploadState({
                status: importResponse.success ? 'success' : 'error',
                fileName: file.name,
                response: importResponse,
            })

            onUploadComplete?.(importResponse)
        } catch (error: any) {
            const errorMessage = error?.response?.data?.error || 'Failed to import file'

            const errorResponse: ImportResponse = {
                success: false,
                imported: 0,
                failed: 0,
                total: 0,
                errors: [
                    {
                        row: 0,
                        field: 'file',
                        message: errorMessage,
                    },
                ],
            }

            setUploadState({
                status: 'error',
                fileName: file.name,
                response: errorResponse,
            })

            onUploadComplete?.(errorResponse)
        }
    }

    const resetUpload = () => {
        setUploadState({ status: 'idle' })
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }
    const downloadTemplate = (type: 'csv' | 'excel' = 'csv') => {
        const headers = ['name', 'address', 'phone', 'email', 'city', 'state', 'website']
        const example = [
            'Provider Name',
            '123 Main St',
            '(555) 123-4567',
            'email@example.com',
            'City',
            'ST',
            'www.example.com',
        ]

        if (type === 'csv') {
            const csvContent = [headers.join(','), example.join(',')].join('\n')
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)

            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'provider_template.csv')
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        }
    }

    return (
        <div className='w-full max-w-2xl p-6 mx-auto'>
            <div className='space-y-4'>
                {uploadState.status === 'idle' && (
                    <>
                        <SelectField
                            inputClass='w-full rounded-xl'
                            optionsListConfig={{
                                endpoint: ROUTES.admin.providerCategories,
                                valueKey: '_id',
                                textKey: 'name',
                            }}
                            required
                            value={selectedCategory}
                            label='Provider Category'
                            onChange={(val) => setSelectedCategory(val)}
                        />
                        <div
                            className={`relative border-2 border-dashed rounded-xl p-8 transition-all ${dragActive ? 'border-brand-500 bg-brand-50' : 'border-gray-300 hover:border-gray-400'}`}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                        >
                            <input
                                ref={fileInputRef}
                                type='file'
                                className='hidden'
                                onChange={handleFileSelect}
                                accept={acceptedFormats.join(',')}
                            />

                            <div className='text-center'>
                                <DocumentArrowUpIcon className='w-12 h-12 mx-auto text-gray-400' />
                                <p className='mt-4 text-lg font-medium text-gray-900'>
                                    Drop your file here, or{' '}
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        className='font-semibold text-brand-600 hover:text-brand-700'
                                    >
                                        browse
                                    </button>
                                </p>
                                <p className='mt-2 text-sm text-gray-500'>
                                    Supports {acceptedFormats.join(', ')} (max {maxFileSize}MB)
                                </p>
                            </div>
                            <div className='flex justify-center w-full mt-4 '>
                                <Button onClick={() => downloadTemplate('csv')} className='text-sm '>
                                    Download template
                                </Button>
                            </div>
                        </div>
                    </>
                )}

                {uploadState.status === 'parsing' && (
                    <div className='p-6 border border-gray-200 rounded-xl bg-gray-50'>
                        <div className='flex items-center space-x-3'>
                            <ArrowPathIcon className='w-5 h-5 text-brand-600 animate-spin' />
                            <div className='flex-1'>
                                <p className='text-sm font-medium text-gray-900'>Processing {uploadState.fileName}</p>
                                <p className='text-sm text-gray-500'>Parsing and validating data...</p>
                            </div>
                        </div>
                    </div>
                )}

                {uploadState.status === 'success' && uploadState.response && (
                    <div className='p-6 border border-green-200 rounded-xl bg-green-50'>
                        <div className='flex items-start space-x-3'>
                            <CheckCircleIcon className='h-5 w-5 text-green-600 mt-0.5' />
                            <div className='flex-1'>
                                <p className='text-sm font-medium text-gray-900'>
                                    Successfully processed {uploadState.fileName}
                                </p>
                                <p className='mt-1 text-sm text-gray-600'>
                                    {uploadState.response.imported} of {uploadState.response.total} providers imported
                                    successfully
                                </p>

                                {uploadState.response.warnings && uploadState.response.warnings.length > 0 && (
                                    <div className='mt-3 space-y-1'>
                                        <p className='text-sm font-medium text-yellow-800'>Warnings:</p>
                                        {uploadState.response.warnings.slice(0, 3).map((warning, i) => (
                                            <p key={i} className='pl-4 text-sm text-yellow-700'>
                                                • {warning}
                                            </p>
                                        ))}
                                        {uploadState.response.warnings.length > 3 && (
                                            <p className='pl-4 text-sm text-yellow-700'>
                                                ... and {uploadState.response.warnings.length - 3} more warnings
                                            </p>
                                        )}
                                    </div>
                                )}

                                {uploadState.response.errors && uploadState.response.errors.length > 0 && (
                                    <div className='mt-3 space-y-1'>
                                        <p className='text-sm font-medium text-orange-800'>Some rows had issues:</p>
                                        {uploadState.response.errors.slice(0, 3).map((error, i) => (
                                            <p key={i} className='pl-4 text-sm text-orange-700'>
                                                • Row {error.row}: {error.message}
                                            </p>
                                        ))}
                                        {uploadState.response.errors.length > 3 && (
                                            <p className='pl-4 text-sm text-orange-700'>
                                                ... and {uploadState.response.errors.length - 3} more errors
                                            </p>
                                        )}
                                    </div>
                                )}

                                <button
                                    onClick={resetUpload}
                                    className='mt-3 text-sm font-medium text-brand-600 hover:text-brand-700'
                                >
                                    Upload another file
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {uploadState.status === 'error' && uploadState.response && (
                    <div className='p-6 border rounded-xl bg-gray-50'>
                        <div className='flex items-start space-x-3'>
                            <ExclamationTriangleIcon className='h-5 w-5 text-red-600 mt-0.5' />
                            <div className='flex-1'>
                                <p className='text-sm font-medium text-gray-900'>
                                    Failed to process {uploadState.fileName}
                                </p>

                                {uploadState.response.imported > 0 && (
                                    <p className='mt-1 text-sm text-gray-600'>
                                        {uploadState.response.imported} of {uploadState.response.total} providers were
                                        imported before errors occurred
                                    </p>
                                )}

                                {uploadState.response.errors && uploadState.response.errors.length > 0 && (
                                    <div className='mt-3 space-y-2'>
                                        <p className='text-sm font-medium text-red-800'>Errors:</p>
                                        <div className='space-y-1 overflow-y-auto max-h-40'>
                                            {uploadState.response.errors.slice(0, 5).map((error, i) => (
                                                <div key={i} className='pl-4 text-sm text-red-700'>
                                                    • {error.row > 0 ? `Row ${error.row}: ` : ''}
                                                    {error.message}
                                                </div>
                                            ))}
                                            {uploadState.response.errors.length > 5 && (
                                                <p className='pl-4 text-sm text-red-700'>
                                                    ... and {uploadState.response.errors.length - 5} more errors
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className='flex mt-3 space-x-3'>
                                    <Button onClick={resetUpload} className='text-sm font-medium'>
                                        Try again
                                    </Button>
                                    <Button onClick={() => downloadTemplate('csv')} className='text-sm '>
                                        Download template
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className='p-4 mt-6 rounded-lg bg-gray-50'>
                    <p className='mb-2 text-sm font-medium text-gray-700'>Expected CSV Format:</p>
                    <div className='p-3 font-mono text-xs text-gray-600 bg-white border border-gray-200 rounded'>
                        <div>name,address,phone,email,city,state,website</div>
                        <div className='text-gray-400'>
                            Provider Name,123 Main St,(555) 123-4567,email@example.com,City,ST,www.example.com
                        </div>
                    </div>
                    <p className='mt-2 text-xs text-gray-500'>* Required fields: name, address</p>
                    <p className='mt-1 text-xs text-gray-500'>
                        * The system will auto-detect column names and format phone numbers
                    </p>
                </div>
            </div>
        </div>
    )
}

export default FileUploadComponent
